import type { ScoProduct } from '@src/graphql-types'
import type { IconType } from 'react-icons'

import { isPlanFeatureSlug, PLAN_FEATURES, planTierFor } from './planFeatures'

/**
 * Why a paywall opened. Membership (free / pro / team) is separate and comes
 * from the caller's subscription. These ids follow the discarded Rutan variant
 * table: an intent recommends one product and filters feature lines.
 */
export type PaywallIntentId =
  | 'default'
  | 'advisor-message'
  | 'advisor-invite'
  | 'leadership-message'
  | 'investor-search'
  | 'deck-slide-lock'
  | 'content-lock'
  | 'wil-limit'
  | 'event-rsvp'
  | 'perk-unlock'
  | 'member-match'
  | 'office-hours'
  | 'premium-event'
  | 'goal:customers'
  | 'goal:customers.first-customers'
  | 'goal:customers.scale-sales'
  | 'goal:funding'
  | 'goal:funding.friends-family'
  | 'goal:funding.angel'
  | 'goal:funding.vc'
  | 'goal:cofounder'
  | 'goal:mvp'
  | 'member-message'
  | 'discussion-join'
  | 'member-directory'
  | 'customers-write'
  | 'board-apply'
  | 'fast-send'
  | 'investor-outreach'

export type PaywallIntentStatus = 'ready' | 'needs-copy' | 'needs-data' | 'blocked'

/** Copy a wall shows. `{token}` names come from `tokens`; `cta` falls back to "Get {plan}" ("Upgrade to {plan}" for a member). */
export type PaywallIntentCopy = {
  headline: string
  supporting: string
  cta?: string
}

export type PaywallIntent = PaywallIntentCopy & {
  id: PaywallIntentId
  /** Token-free copy for the same job, used when a token is blank or the data isn't wired yet. */
  fallback: PaywallIntentCopy
  recommendedProductId: string
  /** Plan-feature slugs (see `planFeatures.ts`), matched as substrings of slug or title. Matches sort first, in this order. */
  promote: string[]
  /** Plan-feature slugs matched the same way. Matches are hidden. */
  suppress: string[]
  /** `{token}` names the copy may use. A blank value switches to `fallback`, never to generic copy. */
  tokens: string[]
  status: PaywallIntentStatus
}

export type PaywallFeature = {
  key: string
  title: string
  description?: string
  /** Bullet from `planFeatures`. Absent on an unknown catalog line, which lists with a check. */
  icon?: IconType
  /** Per-plan amount for the comparison table, such as "15 / day". Absent means included. */
  value?: string
  /** Comparison row label when `title` carries a plan-specific number. */
  compareTitle?: string
  /** False when the line belongs in the compare table and not on a plan card. */
  showOnCard?: boolean
}

export const STARTUPS_PRO_PRODUCT_ID = 'scom-accelerator-tools'
export const ADVISOR_TEAM_PRODUCT_ID = 'scom-accelerator-advisor'

/** Investor-database and outreach lines the Rutan variants call the "investor set". */
const INVESTOR_SET = ['investor-directory', 'unlimited-matches', 'unlimited-outreach', 'messages-per-day']

/** The fundraising lines that explain what a raise-focused buyer gets, daily message cap first. */
const FUNDRAISE_LINES = ['messages-per-day', 'unlimited-matches', 'investor-directory', 'unlimited-outreach']

const PERKS_AND_WORKSHOPS = ['perks', 'live-workshops']

const INVESTOR_AND_PITCH = [...INVESTOR_SET, 'pitch-tools']

const PRO = STARTUPS_PRO_PRODUCT_ID
const TEAM = ADVISOR_TEAM_PRODUCT_ID

function defineIntent(id: PaywallIntentId, fields: Omit<PaywallIntent, 'id'>): PaywallIntent {
  return { id, ...fields }
}

/**
 * One job per intent: the headline names what the member was trying to do, the supporting line
 * says which plan does it and how (only claims `planFeatures.ts` backs), and the CTA repeats the job.
 */
export const PAYWALL_INTENTS: Record<PaywallIntentId, PaywallIntent> = {
  default: defineIntent('default', {
    headline: 'Unlock advisors, investors, and more',
    supporting: 'Startups Pro adds advisor messaging, investor matching, and guided courses to your account.',
    fallback: {
      headline: 'Unlock advisors, investors, and more',
      supporting:
        'Startups Pro adds advisor messaging, investor matching, and guided courses to your account.',
    },
    recommendedProductId: PRO,
    promote: [],
    suppress: [],
    tokens: [],
    status: 'ready',
  }),
  'advisor-message': defineIntent('advisor-message', {
    headline: 'Message {advisorName}',
    supporting:
      'Startups Pro lets you message any marketplace advisor, invite them to your board, and send FAST agreements.',
    cta: 'Message {advisorName}',
    fallback: {
      headline: 'Message this advisor',
      supporting:
        'Startups Pro lets you message any marketplace advisor, invite them to your board, and send FAST agreements.',
      cta: 'Unlock advisor messaging',
    },
    recommendedProductId: PRO,
    promote: ['marketplace-message', 'board-invite', 'fast-send', 'wil-chat'],
    suppress: INVESTOR_SET,
    tokens: ['advisorName'],
    status: 'needs-copy',
  }),
  'advisor-invite': defineIntent('advisor-invite', {
    headline: 'Invite {advisorName} to your board',
    supporting: 'Startups Pro lets you invite marketplace advisors to your board and offer equity with FAST.',
    cta: 'Invite {advisorName}',
    fallback: {
      headline: 'Invite this advisor to your board',
      supporting:
        'Startups Pro lets you invite marketplace advisors to your board and offer equity with FAST.',
      cta: 'Unlock board invites',
    },
    recommendedProductId: PRO,
    promote: ['board-invite', 'marketplace-message', 'fast-send'],
    suppress: INVESTOR_SET,
    tokens: ['advisorName'],
    status: 'needs-copy',
  }),
  'leadership-message': defineIntent('leadership-message', {
    headline: 'Message {execName} and the Startups.com team',
    supporting:
      'Advisor Team adds direct messages with Ed, Ryan, Steve, and Wil, plus team office hours on Events.',
    cta: 'Message {execName}',
    fallback: {
      headline: 'Message the Startups.com team',
      supporting:
        'Advisor Team adds direct messages with Ed, Ryan, Steve, and Wil, plus team office hours on Events.',
      cta: 'Unlock the Startups.com team',
    },
    recommendedProductId: TEAM,
    promote: ['team-dm', 'expert-sessions', 'advisor-sprints', 'messages-per-day'],
    suppress: [],
    tokens: ['execName'],
    status: 'needs-copy',
  }),
  'investor-search': defineIntent('investor-search', {
    headline: 'Reach the {resultCount} investors in this search',
    supporting:
      'Startups Pro reveals investor contacts and sends 3 investor messages a day. Advisor Team sends 15.',
    cta: 'Unlock investor contacts',
    fallback: {
      headline: 'Reach the investors in this search',
      supporting:
        'Startups Pro reveals investor contacts and sends 3 investor messages a day. Advisor Team sends 15.',
      cta: 'Unlock investor contacts',
    },
    recommendedProductId: PRO,
    promote: FUNDRAISE_LINES,
    suppress: PERKS_AND_WORKSHOPS,
    tokens: ['resultCount'],
    status: 'needs-data',
  }),
  'deck-slide-lock': defineIntent('deck-slide-lock', {
    headline: 'Unlock the rest of {deckName}',
    supporting:
      'Startups Pro continues the audit past the free slides and drafts investor emails from your deck.',
    cta: 'Unlock the full audit',
    fallback: {
      headline: 'Unlock the rest of your deck audit',
      supporting:
        'Startups Pro continues the audit past the free slides and drafts investor emails from your deck.',
      cta: 'Unlock the full audit',
    },
    recommendedProductId: PRO,
    promote: ['pitch-tools'],
    suppress: [...PERKS_AND_WORKSHOPS, 'member-matching'],
    tokens: ['deckName'],
    status: 'needs-copy',
  }),
  'content-lock': defineIntent('content-lock', {
    headline: 'Continue {contentName}',
    supporting: 'Startups Pro unlocks the rest, plus guided courses, live workshops, and peer groups.',
    cta: 'Unlock {contentName}',
    fallback: {
      headline: 'Pick up where you left off',
      supporting: 'Startups Pro unlocks the rest, plus guided courses, live workshops, and peer groups.',
      cta: 'Unlock the rest',
    },
    recommendedProductId: PRO,
    promote: ['courses', 'live-workshops', 'goals'],
    suppress: INVESTOR_SET,
    tokens: ['contentName'],
    status: 'ready',
  }),
  // Caller supplies messageCount. The Advisors paywall merchandises 50; this template does not.
  'wil-limit': defineIntent('wil-limit', {
    headline: "You've used your {messageCount} free Wil.ai messages",
    supporting:
      'Startups Pro removes the cap, so you can keep chatting with Wil.ai and message real advisors.',
    cta: 'Keep chatting with Wil.ai',
    fallback: {
      headline: "You've used your free Wil.ai messages",
      supporting:
        'Startups Pro removes the cap, so you can keep chatting with Wil.ai and message real advisors.',
      cta: 'Keep chatting with Wil.ai',
    },
    recommendedProductId: PRO,
    promote: ['wil-chat', 'marketplace-message'],
    suppress: INVESTOR_SET,
    tokens: ['messageCount'],
    status: 'ready',
  }),
  'event-rsvp': defineIntent('event-rsvp', {
    headline: 'Attend {eventName}',
    supporting: 'Startups Pro unlocks this event, along with live workshops and peer groups.',
    cta: 'Save my spot',
    fallback: {
      headline: 'Attend this event',
      supporting: 'Startups Pro unlocks this event, along with live workshops and peer groups.',
      cta: 'Save my spot',
    },
    recommendedProductId: PRO,
    promote: ['live-workshops'],
    suppress: INVESTOR_SET,
    tokens: ['eventName'],
    status: 'ready',
  }),
  'perk-unlock': defineIntent('perk-unlock', {
    headline: 'Unlock {partnerName}',
    supporting: 'Startups Pro includes over $25,000 in partner perks.',
    cta: 'Unlock this perk',
    fallback: {
      headline: 'Unlock this partner perk',
      supporting: 'Startups Pro includes over $25,000 in partner perks.',
      cta: 'Unlock this perk',
    },
    recommendedProductId: PRO,
    promote: ['perks'],
    suppress: INVESTOR_SET,
    tokens: ['partnerName'],
    status: 'ready',
  }),
  'member-match': defineIntent('member-match', {
    headline: 'Message your member matches',
    supporting: 'Startups Pro adds member matching and intros across a global network of founders.',
    cta: 'Unlock member intros',
    fallback: {
      headline: 'Message your member matches',
      supporting: 'Startups Pro adds member matching and intros across a global network of founders.',
      cta: 'Unlock member intros',
    },
    recommendedProductId: PRO,
    promote: ['member-matching', 'global-founders'],
    suppress: INVESTOR_AND_PITCH,
    tokens: [],
    status: 'ready',
  }),
  'office-hours': defineIntent('office-hours', {
    headline: 'Join office hours with {hostName}',
    supporting: 'Advisor Team includes team office hours with Ed, Ryan, Steve, and Wil on Events.',
    cta: 'Join office hours',
    fallback: {
      headline: 'Join team office hours',
      supporting: 'Advisor Team includes team office hours with Ed, Ryan, Steve, and Wil on Events.',
      cta: 'Join office hours',
    },
    recommendedProductId: TEAM,
    promote: ['expert-sessions', 'team-dm'],
    suppress: [],
    tokens: ['hostName'],
    status: 'ready',
  }),
  'premium-event': defineIntent('premium-event', {
    headline: 'Attend {eventName}',
    supporting: 'Advisor Team includes premium events and advisor-led sprints.',
    cta: 'Save my spot',
    fallback: {
      headline: 'Attend this premium event',
      supporting: 'Advisor Team includes premium events and advisor-led sprints.',
      cta: 'Save my spot',
    },
    recommendedProductId: TEAM,
    promote: ['advisor-sprints', 'expert-sessions'],
    suppress: [],
    tokens: ['eventName'],
    status: 'needs-copy',
  }),
  'goal:customers': defineIntent('goal:customers', {
    headline: 'Find and win customers',
    supporting: 'Startups Pro adds guided courses, live workshops, and peer groups for this goal.',
    cta: 'Continue this goal',
    fallback: {
      headline: 'Find and win customers',
      supporting: 'Startups Pro adds guided courses, live workshops, and peer groups for this goal.',
      cta: 'Continue this goal',
    },
    recommendedProductId: PRO,
    promote: ['goals', 'courses', 'live-workshops'],
    suppress: INVESTOR_AND_PITCH,
    tokens: [],
    status: 'needs-copy',
  }),
  'goal:customers.first-customers': defineIntent('goal:customers.first-customers', {
    headline: 'Find your first customers',
    supporting: 'Startups Pro adds guided courses and live workshops for landing your first customers.',
    cta: 'Continue this goal',
    fallback: {
      headline: 'Find your first customers',
      supporting: 'Startups Pro adds guided courses and live workshops for landing your first customers.',
      cta: 'Continue this goal',
    },
    recommendedProductId: PRO,
    promote: ['goals', 'courses', 'live-workshops'],
    suppress: INVESTOR_AND_PITCH,
    tokens: [],
    status: 'needs-copy',
  }),
  'goal:customers.scale-sales': defineIntent('goal:customers.scale-sales', {
    headline: 'Scale your sales',
    supporting: 'Startups Pro adds guided courses, live workshops, and peer groups for growing sales.',
    cta: 'Continue this goal',
    fallback: {
      headline: 'Scale your sales',
      supporting: 'Startups Pro adds guided courses, live workshops, and peer groups for growing sales.',
      cta: 'Continue this goal',
    },
    recommendedProductId: PRO,
    promote: ['goals', 'courses', 'live-workshops'],
    suppress: INVESTOR_AND_PITCH,
    tokens: [],
    status: 'blocked',
  }),
  'goal:funding': defineIntent('goal:funding', {
    headline: 'Get ready to raise',
    supporting: 'Startups Pro adds curated investor matches and 3 investor messages a day.',
    cta: 'Unlock investor matching',
    fallback: {
      headline: 'Get ready to raise',
      supporting: 'Startups Pro adds curated investor matches and 3 investor messages a day.',
      cta: 'Unlock investor matching',
    },
    recommendedProductId: PRO,
    promote: [...FUNDRAISE_LINES, 'pitch-tools'],
    suppress: PERKS_AND_WORKSHOPS,
    tokens: [],
    status: 'needs-copy',
  }),
  // This path deliberately hides the investor database; the pitch tools are the offer.
  'goal:funding.friends-family': defineIntent('goal:funding.friends-family', {
    headline: 'Raise from friends and family',
    supporting:
      'Startups Pro helps you prepare the ask with the pitch deck auditor, email builder, and guided courses.',
    cta: 'Prepare my pitch',
    fallback: {
      headline: 'Raise from friends and family',
      supporting:
        'Startups Pro helps you prepare the ask with the pitch deck auditor, email builder, and guided courses.',
      cta: 'Prepare my pitch',
    },
    recommendedProductId: PRO,
    promote: ['pitch-tools', 'goals', 'courses'],
    suppress: INVESTOR_SET,
    tokens: [],
    status: 'needs-copy',
  }),
  'goal:funding.angel': defineIntent('goal:funding.angel', {
    headline: 'Reach angel investors in {industry}',
    supporting: 'Startups Pro matches you with investors and sends 3 investor messages a day.',
    cta: 'Unlock investor matching',
    fallback: {
      headline: 'Reach angel investors',
      supporting: 'Startups Pro matches you with investors and sends 3 investor messages a day.',
      cta: 'Unlock investor matching',
    },
    recommendedProductId: PRO,
    promote: ['unlimited-matches', 'messages-per-day', 'investor-directory', 'unlimited-outreach'],
    suppress: PERKS_AND_WORKSHOPS,
    tokens: ['industry'],
    status: 'needs-data',
  }),
  'goal:funding.vc': defineIntent('goal:funding.vc', {
    headline: 'Prepare for institutional investors',
    supporting:
      'Advisor Team sends 15 investor messages a day and adds office hours with the Startups.com team.',
    cta: 'Raise with Advisor Team',
    fallback: {
      headline: 'Prepare for institutional investors',
      supporting:
        'Advisor Team sends 15 investor messages a day and adds office hours with the Startups.com team.',
      cta: 'Raise with Advisor Team',
    },
    recommendedProductId: TEAM,
    promote: ['messages-per-day', 'expert-sessions', 'team-dm', 'unlimited-matches'],
    suppress: PERKS_AND_WORKSHOPS,
    tokens: [],
    status: 'needs-copy',
  }),
  'goal:cofounder': defineIntent('goal:cofounder', {
    headline: 'Find a co-founder',
    supporting: 'Startups Pro adds member matching and intros across a global network of founders.',
    cta: 'Unlock member matching',
    fallback: {
      headline: 'Find a co-founder',
      supporting: 'Startups Pro adds member matching and intros across a global network of founders.',
      cta: 'Unlock member matching',
    },
    recommendedProductId: PRO,
    promote: ['member-matching', 'global-founders'],
    suppress: INVESTOR_SET,
    tokens: [],
    status: 'needs-copy',
  }),
  'goal:mvp': defineIntent('goal:mvp', {
    headline: 'Build your MVP',
    supporting:
      'Startups Pro adds guided courses, live workshops, and peer groups for building your first version.',
    cta: 'Continue this goal',
    fallback: {
      headline: 'Build your MVP',
      supporting:
        'Startups Pro adds guided courses, live workshops, and peer groups for building your first version.',
      cta: 'Continue this goal',
    },
    recommendedProductId: PRO,
    promote: ['courses', 'live-workshops', 'goals'],
    suppress: INVESTOR_SET,
    tokens: [],
    status: 'blocked',
  }),

  'member-message': defineIntent('member-message', {
    headline: 'Message {memberName}',
    supporting: 'Startups Pro unlocks direct messages and intros with founders across the community.',
    cta: 'Message {memberName}',
    fallback: {
      headline: 'Message this founder',
      supporting: 'Startups Pro unlocks direct messages and intros with founders across the community.',
      cta: 'Unlock messaging',
    },
    recommendedProductId: PRO,
    promote: ['member-matching', 'global-founders', 'marketplace-message'],
    suppress: INVESTOR_AND_PITCH,
    tokens: ['memberName'],
    status: 'ready',
  }),
  'discussion-join': defineIntent('discussion-join', {
    headline: 'Join the discussion',
    supporting: 'Any paid plan lets you comment and trade answers with other founders in every discussion.',
    cta: 'Join the discussion',
    fallback: {
      headline: 'Join the discussion',
      supporting: 'Any paid plan lets you comment and trade answers with other founders in every discussion.',
      cta: 'Join the discussion',
    },
    recommendedProductId: PRO,
    promote: ['global-founders', 'live-workshops', 'member-matching'],
    suppress: INVESTOR_SET,
    tokens: [],
    status: 'ready',
  }),
  'member-directory': defineIntent('member-directory', {
    headline: 'See the full founder directory',
    supporting: 'Startups Pro opens the full member directory, plus member matching and intros.',
    cta: 'Unlock the directory',
    fallback: {
      headline: 'See the full founder directory',
      supporting: 'Startups Pro opens the full member directory, plus member matching and intros.',
      cta: 'Unlock the directory',
    },
    recommendedProductId: PRO,
    promote: ['global-founders', 'member-matching'],
    suppress: INVESTOR_AND_PITCH,
    tokens: [],
    status: 'ready',
  }),
  'customers-write': defineIntent('customers-write', {
    headline: 'Build your customer plan',
    supporting:
      'Startups Pro unlocks Get Customers: ideal customer profiles, segments, channels, and early adopters.',
    cta: 'Unlock Get Customers',
    fallback: {
      headline: 'Build your customer plan',
      supporting:
        'Startups Pro unlocks Get Customers: ideal customer profiles, segments, channels, and early adopters.',
      cta: 'Unlock Get Customers',
    },
    recommendedProductId: PRO,
    promote: ['goals', 'courses', 'live-workshops'],
    suppress: INVESTOR_SET,
    tokens: [],
    status: 'ready',
  }),
  'board-apply': defineIntent('board-apply', {
    headline: 'Apply for this board seat',
    supporting:
      'Startups Pro lets you apply to open board seats, message founders, and send FAST agreements.',
    cta: 'Apply for this seat',
    fallback: {
      headline: 'Apply for this board seat',
      supporting:
        'Startups Pro lets you apply to open board seats, message founders, and send FAST agreements.',
      cta: 'Apply for this seat',
    },
    recommendedProductId: PRO,
    promote: ['board-invite', 'fast-send', 'marketplace-message'],
    suppress: INVESTOR_SET,
    tokens: [],
    status: 'ready',
  }),
  'fast-send': defineIntent('fast-send', {
    headline: 'Send this FAST agreement',
    supporting:
      'Startups Pro lets you offer advisor equity on the standard FAST agreement and track it to signature.',
    cta: 'Send FAST',
    fallback: {
      headline: 'Send this FAST agreement',
      supporting:
        'Startups Pro lets you offer advisor equity on the standard FAST agreement and track it to signature.',
      cta: 'Send FAST',
    },
    recommendedProductId: PRO,
    promote: ['fast-send', 'board-invite', 'marketplace-message'],
    suppress: INVESTOR_SET,
    tokens: [],
    status: 'ready',
  }),
  'investor-outreach': defineIntent('investor-outreach', {
    headline: 'Send outreach to your investor matches',
    supporting:
      'Startups Pro sends 3 investor messages a day from your own email, with AI-drafted emails. Advisor Team sends 15.',
    cta: 'Unlock investor outreach',
    fallback: {
      headline: 'Send outreach to your investor matches',
      supporting:
        'Startups Pro sends 3 investor messages a day from your own email, with AI-drafted emails. Advisor Team sends 15.',
      cta: 'Unlock investor outreach',
    },
    recommendedProductId: PRO,
    promote: FUNDRAISE_LINES,
    suppress: PERKS_AND_WORKSHOPS,
    tokens: [],
    status: 'ready',
  }),
}

/** What a wall says about a plan when the intent names a different plan, or none. */
export const PLAN_PITCHES: Record<string, PaywallIntentCopy> = {
  [PRO]: {
    headline: 'Unlock advisors, investors, and more',
    supporting: 'Startups Pro adds advisor messaging, investor matching, and guided courses to your account.',
  },
  [TEAM]: {
    headline: 'Work directly with the Startups.com team',
    supporting:
      'Advisor Team adds direct messages with Ed, Ryan, Steve, and Wil, team office hours, and 15 investor messages a day.',
  },
}

export function getPaywallIntent(id?: PaywallIntentId | null): PaywallIntent {
  if (id && PAYWALL_INTENTS[id]) {
    return PAYWALL_INTENTS[id]
  }
  return PAYWALL_INTENTS.default
}

function applyTokens(template: string, tokens: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (match, name: string) => {
    const value = tokens[name]?.trim()
    return value ? value : match
  })
}

function missingTokens(intent: PaywallIntent, tokens: Record<string, string>): boolean {
  return intent.tokens.some(name => !tokens[name]?.trim())
}

export type ResolvedPaywallIntent = PaywallIntent & {
  /** True when the intent's token-free `fallback` copy (or `default`, for a blocked intent) is showing. */
  usedFallbackCopy: boolean
}

/**
 * A blocked intent is not ready to sell, so it shows `default` copy and filters.
 * Blank tokens or unwired data (`needs-data`) keep the intent's job and filters with its
 * token-free fallback copy. Unresolved `{token}` text never reaches the screen.
 */
export function resolvePaywallIntent(
  id?: PaywallIntentId | null,
  tokens?: Record<string, string>
): ResolvedPaywallIntent {
  const intent = getPaywallIntent(id)
  if (intent.status === 'blocked') {
    return { ...PAYWALL_INTENTS.default, usedFallbackCopy: true }
  }
  const provided = tokens ?? {}
  if (intent.status === 'needs-data' || missingTokens(intent, provided)) {
    return { ...intent, ...intent.fallback, usedFallbackCopy: true }
  }
  return {
    ...intent,
    headline: applyTokens(intent.headline, provided),
    supporting: applyTokens(intent.supporting, provided),
    cta: intent.cta ? applyTokens(intent.cta, provided) : undefined,
    usedFallbackCopy: false,
  }
}

type RawFeature = {
  slug?: string | null
  title?: string | null
  description?: string | null
  icon?: IconType
  value?: string
  compareTitle?: string
  showOnCard?: boolean
}

function featureHaystack(feature: RawFeature): string {
  return `${feature.slug ?? ''} ${feature.title ?? ''}`.toLowerCase()
}

function matchesAny(haystack: string, needles: string[]): boolean {
  return needles.some(needle => haystack.includes(needle.toLowerCase()))
}

function toPaywallFeature(feature: RawFeature, index: number): PaywallFeature | null {
  const title = feature.title?.trim()
  if (!title) {
    return null
  }
  const description = feature.description?.trim()
  return {
    key: feature.slug?.trim() || `${title}-${index}`,
    title,
    description: description || undefined,
    icon: feature.icon,
    value: feature.value,
    compareTitle: feature.compareTitle,
    showOnCard: feature.showOnCard,
  }
}

function catalogFeatures(product: ScoProduct): RawFeature[] {
  const fromPaywall = (product.paywallOptions?.features ?? []).filter(
    (feature): feature is NonNullable<typeof feature> => Boolean(feature?.title?.trim())
  )
  if (fromPaywall.length > 0) {
    return fromPaywall
  }
  return (product.features ?? []).flatMap(feature => {
    const title = feature?.name?.trim()
    if (!title) {
      return []
    }
    return [{ slug: feature?.id, title }]
  })
}

/**
 * Pro and Advisor Team read their lines from `planFeatures.ts`, so every wall explains the same
 * purchase, including per-plan amounts. Catalog lines with an unknown slug still show after them.
 * Other products keep their catalog lines as-is.
 */
function rawFeatures(product: ScoProduct): RawFeature[] {
  const catalog = catalogFeatures(product)
  const tier = planTierFor(product.productId)
  if (!tier) {
    return catalog
  }
  const planned = PLAN_FEATURES.flatMap(feature => {
    const line = feature.plans[tier]
    return line
      ? [
          {
            slug: feature.slug,
            title: line.title,
            description: feature.description,
            icon: feature.icon,
            value: line.value,
            compareTitle: feature.compareTitle,
            showOnCard: line.showOnCard,
          },
        ]
      : []
  })
  return [...planned, ...catalog.filter(feature => !isPlanFeatureSlug(feature.slug))]
}

export function visiblePaywallFeatures(product: ScoProduct, intent: PaywallIntent): PaywallFeature[] {
  const ranked = rawFeatures(product)
    .map((feature, index) => ({ feature, index, haystack: featureHaystack(feature) }))
    .filter(item => !matchesAny(item.haystack, intent.suppress))

  const promoted: typeof ranked = []
  const promotedKeys = new Set<number>()
  for (const needle of intent.promote) {
    const item = ranked.find(candidate => candidate.haystack.includes(needle.toLowerCase()))
    if (item && !promotedKeys.has(item.index)) {
      promotedKeys.add(item.index)
      promoted.push(item)
    }
  }
  const rest = ranked.filter(item => !promotedKeys.has(item.index))

  return [...promoted, ...rest].flatMap(item => {
    const next = toPaywallFeature(item.feature, item.index)
    return next ? [next] : []
  })
}

export function isRecommendedProduct(product: ScoProduct, intent: PaywallIntent): boolean {
  return Boolean(product.productId) && product.productId === intent.recommendedProductId
}

/** Recommended plan first. Relative order of the others stays put. */
export function orderProductsForIntent(products: ScoProduct[], intent: PaywallIntent): ScoProduct[] {
  const recommended = products.filter(product => isRecommendedProduct(product, intent))
  const rest = products.filter(product => !isRecommendedProduct(product, intent))
  return [...recommended, ...rest]
}
